/**
 * MpApp.tsx —— 多人对战子应用（模式入口内的独立流程）。
 *
 * 路由：setup（创建/加入）→ lobby（大厅选座准备）→ game（对局，赢率门控）。
 * 复盘赢率仅在本手结束后由玩家显式打开（对局进行中入口不可见/不可用）。
 */

import { useEffect, useSyncExternalStore } from 'react';
import { mpClient, type MpState } from '../../net/mpClient';
import { setLetwayFloat } from '../../state/letway';
import { RoomSetup } from './RoomSetup';
import { Lobby } from './Lobby';
import { MpTable } from './MpTable';
import { MpSyncOverlay } from './mpVisuals';
import './mp.css';

export function useMp(): MpState {
  return useSyncExternalStore(mpClient.store.subscribe, mpClient.store.get, mpClient.store.get);
}

export function MpApp({ onExit }: { onExit: () => void }) {
  const s = useMp();

  const leaveToHome = () => { mpClient.leave(); onExit(); };
  // 刷新恢复中 / 断线重连中：全屏毛玻璃遮罩「正在同步对局状态…」（设计稿 §4.4）
  const syncing = s.resuming || s.conn === 'reconnecting';

  /**
   * 二期 FR-E2：同步遮罩（mp-sync）是多人关键浮层。在场期间登记到让位中枢，
   * 触发 toast 静默排队 + pwa 让位，保证同步遮罩关键区不被 toast/pwa 从 body 根盖穿（AC-E2.3）。
   */
  useEffect(() => {
    setLetwayFloat('mp-sync', syncing);
    return () => setLetwayFloat('mp-sync', false);
  }, [syncing]);

  return (
    <div className="mp-root">
      {s.screen === 'setup' && <RoomSetup state={s} onBack={onExit} />}
      {s.screen === 'lobby' && <Lobby state={s} onLeave={leaveToHome} />}
      {s.screen === 'game' && <MpTable state={s} onLeave={leaveToHome} />}
      {syncing && <MpSyncOverlay text={s.resuming ? '正在同步对局状态…' : '网络中断，正在同步对局状态…'} />}
    </div>
  );
}
